import { ML_ORDER_STATUS_LABELS, mlOrderAction, type MarketplaceOrderAction } from "./marketplace";
import { SHOPEE_ORDER_STATUS_LABELS, shopeeOrderAction } from "./shopee";
import {
  CHANNEL_PROVIDER_IDS,
  channelOrderAction,
  channelOrderStatusLabel,
  isChannelProvider,
} from "./channels";

/**
 * Marketplaces e canais integrados (T1.16 Mercado Livre, T1.17 Shopee, T3.3 Magalu, Amazon, Nuvemshop e Shopify): o
 * que muda de um para outro nas regras comuns.
 */
export const MARKETPLACE_PROVIDER_IDS = ["mercadolivre", "shopee", ...CHANNEL_PROVIDER_IDS] as const;
export type MarketplaceProvider = (typeof MARKETPLACE_PROVIDER_IDS)[number];

type Names = { label: string; of: string; in: string; article: "o" | "a"; prefix: string };
const NAMES: Record<MarketplaceProvider, Names> = {
  mercadolivre: { label: "Mercado Livre", of: "do", in: "no", article: "o", prefix: "ml" },
  shopee: { label: "Shopee", of: "da", in: "na", article: "a", prefix: "shopee" },
  magalu: { label: "Magalu", of: "do", in: "no", article: "o", prefix: "magalu" },
  amazon: { label: "Amazon", of: "da", in: "na", article: "a", prefix: "amazon" },
  nuvemshop: { label: "Nuvemshop", of: "da", in: "na", article: "a", prefix: "nuvemshop" },
  shopify: { label: "Shopify", of: "da", in: "na", article: "a", prefix: "shopify" },
};

export const MARKETPLACE_LABELS: Readonly<Record<MarketplaceProvider, string>> = Object.fromEntries(
  MARKETPLACE_PROVIDER_IDS.map((p) => [p, NAMES[p].label]),
) as Record<MarketplaceProvider, string>;

/** "do Mercado Livre" / "da Shopee" (mensagens em pt-BR). */
export const marketplaceOf = (p: MarketplaceProvider): string => `${NAMES[p].of} ${NAMES[p].label}`;

/** "no Mercado Livre" / "na Shopee". */
export const marketplaceIn = (p: MarketplaceProvider): string => `${NAMES[p].in} ${NAMES[p].label}`;

/** "O Mercado Livre" / "A Shopee" (início de frase). */
export const marketplaceThe = (p: MarketplaceProvider): string =>
  `${NAMES[p].article.toUpperCase()} ${NAMES[p].label}`;

/** Prefixo das mensagens da fila Q_INTEGRATIONS (`ml.sync`, `shopee.sync`, `nuvemshop.poll`…). */
export const marketplaceMessagePrefix = (p: MarketplaceProvider): string => NAMES[p].prefix;

/** Provedor de um prefixo de mensagem; desconhecido = null. */
export function marketplaceOfPrefix(prefix: string): MarketplaceProvider | null {
  return MARKETPLACE_PROVIDER_IDS.find((p) => NAMES[p].prefix === prefix) ?? null;
}

/** O Mercado Livre exige o mesmo preço em todas as variações do anúncio; os outros aceitam preço por variação. */
export const marketplaceSinglePricePerItem = (p: MarketplaceProvider): boolean => p === "mercadolivre";

export function marketplaceOrderAction(p: MarketplaceProvider, status: string): MarketplaceOrderAction {
  if (isChannelProvider(p)) return channelOrderAction(p, status);
  return p === "shopee" ? shopeeOrderAction(status) : mlOrderAction(status);
}

export function marketplaceOrderStatusLabel(p: MarketplaceProvider, status: string): string {
  if (isChannelProvider(p)) return channelOrderStatusLabel(p, status);
  const labels = p === "shopee" ? SHOPEE_ORDER_STATUS_LABELS : ML_ORDER_STATUS_LABELS;
  return Object.hasOwn(labels, status) ? labels[status]! : status;
}
